import { ILanguageModel } from "@kuika/kuika-cl-model";
import classNames from "classnames";
import { useMemo, useState } from "react";
import { getLanguageFlagSrc } from "../../../shared/utility/designer-languages";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaPopover from "../../ui-kit/kuika-popover/kuika-popover";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallText from "../../ui-kit/typography/SmallText/SmallText";
import { IEmailLanguageOption } from "./email-templates";

/**
 * One control for everything multi-language, replacing the switch + locale select +
 * "Add Language" link the header used to carry.
 *
 * The three were never independent: the switch alone did nothing until a locale existed,
 * so a template could sit in an enabled-with-no-languages state that looked broken. Here
 * adding the first locale IS turning it on, and turning it off lives in the same menu as
 * the locales it turns off. It also drops two controls from a header that was already
 * wrapping its own labels.
 *
 * "ML" is gone from the copy too: the abbreviation reads as machine learning, which is an
 * easy thing to believe next to this product's AI panel.
 */

export interface INotificationTemplateLanguageControlProps {
  isMlEnabled: boolean;
  languages: ILanguageModel[];
  currentLanguage: ILanguageModel | undefined;
  /** The locales not already on this template. */
  addableLanguages: IEmailLanguageOption[];
  onChangeLanguage: (shortName: string) => void;
  onAddLanguage: (language: IEmailLanguageOption) => void;
  onDisableMultiLanguage: () => void;
}

type LanguagePanelView = "list" | "add";

/**
 * The flag for a locale, or the language glyph for the synthetic "Default" entry, which is
 * the template's top-level design rather than any one country.
 */
const LanguageGlyph = ({ localeCode }: { localeCode?: string }) => {
  const flagSrc: string | undefined = getLanguageFlagSrc(localeCode);
  if (!flagSrc) return <KuikaSvg name="language" type="sidebar-icons" width={14} height={14} />;
  return <img className="kuika-email-languages__flag" src={flagSrc} alt="" aria-hidden="true" />;
};

const NotificationTemplateLanguageControl = ({
  isMlEnabled,
  languages,
  currentLanguage,
  addableLanguages,
  onChangeLanguage,
  onAddLanguage,
  onDisableMultiLanguage
}: INotificationTemplateLanguageControlProps) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [view, setView] = useState<LanguagePanelView>("list");
  const [query, setQuery] = useState<string>("");

  const matches: IEmailLanguageOption[] = useMemo((): IEmailLanguageOption[] => {
    const needle: string = query.trim().toLowerCase();
    if (!needle) return addableLanguages;
    return addableLanguages.filter(
      (language: IEmailLanguageOption): boolean =>
        language.label.toLowerCase().includes(needle) || language.value.toLowerCase().includes(needle)
    );
  }, [addableLanguages, query]);

  // Display order only: the base design first, then alphabetical. The stored order is the
  // order locales were added, which is meaningless to read down.
  const orderedLanguages: ILanguageModel[] = useMemo(
    (): ILanguageModel[] =>
      [...languages].sort((a: ILanguageModel, b: ILanguageModel): number => {
        if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
        return a.name.localeCompare(b.name);
      }),
    [languages]
  );

  const close = (): void => {
    setVisible(false);
    setView("list");
    setQuery("");
  };

  // Opening straight onto the locale list when there is nothing to switch between yet:
  // the first gesture a template needs is "add one", not "choose among none".
  const open = (): void => {
    setView(isMlEnabled ? "list" : "add");
    setQuery("");
    setVisible(true);
  };

  const handleSelect = (language: ILanguageModel): void => {
    close();
    if (language.shortName === currentLanguage?.shortName) return;
    onChangeLanguage(language.shortName);
  };

  const handleAdd = (language: IEmailLanguageOption): void => {
    close();
    onAddLanguage(language);
  };

  const handleDisable = (): void => {
    close();
    onDisableMultiLanguage();
  };

  const renderList = (): React.ReactNode => (
    <>
      <KuikaSmallText className="kuika-email-languages__eyebrow">Languages</KuikaSmallText>
      <div className="kuika-email-languages__list">
        {orderedLanguages.map((language: ILanguageModel) => {
          const isCurrent: boolean = language.shortName === currentLanguage?.shortName;
          return (
            <button
              key={language.id}
              type="button"
              className={classNames("kuika-email-languages__item", {
                "kuika-email-languages__item--current": isCurrent
              })}
              data-testid={`${language.shortName}LanguageItem`}
              onClick={(): void => handleSelect(language)}
            >
              <LanguageGlyph localeCode={language.isDefault ? undefined : language.shortName} />
              <span className="kuika-email-languages__item-name">{language.name}</span>
              <KuikaSmallText className="kuika-email-languages__code">
                {language.isDefault ? "Fallback" : language.shortName}
              </KuikaSmallText>
              {isCurrent && (
                <KuikaSvg
                  className="kuika-email-languages__check"
                  name="check"
                  width={14}
                  height={14}
                  color="var(--blue-6)"
                />
              )}
            </button>
          );
        })}
      </div>
      <div className="kuika-email-languages__footer">
        {addableLanguages.length > 0 && (
          <button
            type="button"
            className="kuika-email-languages__action"
            data-testid="addLanguageButton"
            onClick={(): void => {
              setView("add");
              setQuery("");
            }}
          >
            <KuikaSvg name="plus" width={14} height={14} />
            Add language
          </button>
        )}
        <button
          type="button"
          className="kuika-email-languages__action kuika-email-languages__action--off"
          data-testid="disableMultiLanguageButton"
          onClick={handleDisable}
        >
          <KuikaSvg name="x" width={14} height={14} />
          Turn off multi-language
        </button>
      </div>
    </>
  );

  const renderAdd = (): React.ReactNode => (
    <>
      <div className="kuika-email-languages__panel-head">
        {isMlEnabled && (
          <button
            type="button"
            className="kuika-email-languages__back"
            aria-label="Back to languages"
            onClick={(): void => setView("list")}
          >
            <KuikaSvg name="arrow-left" type="arrows" width={14} height={14} />
          </button>
        )}
        <KuikaSmallText className="kuika-email-languages__eyebrow">Add language</KuikaSmallText>
      </div>
      <KuikaInput
        className="kuika-email-languages__search"
        size="md"
        placeholder="Search languages..."
        value={query}
        autoFocus
        leftIcon={<KuikaSvg name="search" width={13} height={13} color="var(--dark-5)" />}
        onChange={(_e: React.ChangeEvent<HTMLInputElement>, value: string | number): void => setQuery(String(value))}
        data-testid="languageSearchInput"
      />
      <div className="kuika-email-languages__list">
        {matches.length === 0 && <span className="kuika-email-languages__no-match">No language matched</span>}
        {matches.map((language: IEmailLanguageOption) => (
          <button
            key={language.value}
            type="button"
            className="kuika-email-languages__item"
            data-testid={`${language.value}AddLanguageItem`}
            onClick={(): void => handleAdd(language)}
          >
            <LanguageGlyph localeCode={language.value} />
            <span className="kuika-email-languages__item-name">{language.label}</span>
            <KuikaSmallText className="kuika-email-languages__code">{language.value}</KuikaSmallText>
          </button>
        ))}
      </div>
    </>
  );

  return (
    <KuikaPopover
      visible={visible}
      onVisibleChange={(next: boolean): void => (next ? open() : close())}
      align="end"
      hideCloseIcon
      className="kuika-email-languages__popover"
      content={<div className="kuika-email-languages__panel">{view === "add" ? renderAdd() : renderList()}</div>}
    >
      <button
        type="button"
        className={classNames("kuika-email-languages__trigger", {
          "kuika-email-languages__trigger--open": visible,
          "kuika-email-languages__trigger--enabled": isMlEnabled,
          // Editing a locale rather than the base design is the state worth shouting
          // about: writing into the wrong language is the expensive mistake here.
          "kuika-email-languages__trigger--locale": isMlEnabled && currentLanguage?.isDefault === false
        })}
        data-testid="emailBuilderLanguageControl"
        aria-label={isMlEnabled ? "Languages" : "Add language"}
      >
        {isMlEnabled ? (
          <LanguageGlyph localeCode={currentLanguage?.isDefault ? undefined : currentLanguage?.shortName} />
        ) : (
          <KuikaSvg name="language" type="sidebar-icons" width={14} height={14} />
        )}
        {isMlEnabled ? (
          <>
            <span className="kuika-email-languages__trigger-name">{currentLanguage?.name ?? "Default"}</span>
            <span className="kuika-email-languages__count">{languages.length}</span>
            <KuikaSvg name={visible ? "chevron-up" : "chevron-down"} type="arrows" width={12} height={12} />
          </>
        ) : (
          <span className="kuika-email-languages__trigger-name">Add language</span>
        )}
      </button>
    </KuikaPopover>
  );
};

export default NotificationTemplateLanguageControl;
